import { useCallback, useEffect } from 'react';

import { createHook, createStore } from 'react-sweet-state';

import FeatureGates from '@atlaskit/feature-gate-js-client';

import { LS_USER_PREFERENCES_KEY } from '../../common/constants/private';
import { useProfileController } from '../profile';

import { type UserPreferences } from './types';

export const getUserPreferencesFromLS = (aaid: string): UserPreferences | undefined => {
	const preferences: Record<string, UserPreferences> = JSON.parse(
		localStorage.getItem(LS_USER_PREFERENCES_KEY) || '{}',
	);

	return preferences[aaid];
};

const store = createStore({
	initialState: {
		preferences: {
			// ideally we want to set the preference in the react component because
			// FF evaluated in module level may not resolve to correct value during SSR
			// however because there is localStorage setting and `firstLoad`, it requires a bit of refactoring to do it
			// this feature is only needed for dogfooding, so may not worth the refactor
			storeUserMessages:
				FeatureGates.initializeCalled() && FeatureGates.checkGate('ai_mate_show_store_messages'),
			useEditorContext: true,
		} as UserPreferences,
		firstLoad: true,
	},
	actions: {
		updatePreferences:
			(data: Partial<UserPreferences>) =>
			({ setState, getState }) => {
				setState({
					preferences: {
						...getState().preferences,
						...data,
					},
					firstLoad: false,
				});
			},
	},
});

const useStore = createHook(store);

export const useUserPreferences = () => {
	const { aaid } = useProfileController();
	const [{ preferences, firstLoad }, { updatePreferences: storeUpdatePreferences }] = useStore();

	const updatePreferences = useCallback(
		(data: Partial<UserPreferences>) => {
			if (!aaid) {
				return;
			}

			storeUpdatePreferences(data);

			localStorage.setItem(
				LS_USER_PREFERENCES_KEY,
				JSON.stringify({
					[aaid]: {
						...preferences,
						...data,
					},
				}),
			);
		},
		[aaid, preferences, storeUpdatePreferences],
	);

	const setStoreUserMessages = useCallback(
		(storeUserMessages: boolean) => {
			updatePreferences({ storeUserMessages });
		},
		[updatePreferences],
	);

	const setUseEditorContext = useCallback(
		(useEditorContext: boolean) => {
			updatePreferences({ useEditorContext });
		},
		[updatePreferences],
	);

	useEffect(() => {
		if (aaid && firstLoad) {
			const preferences = getUserPreferencesFromLS(aaid);

			if (preferences) {
				updatePreferences(preferences);
			}
		}
	}, [aaid, firstLoad, updatePreferences]);

	return {
		preferences,
		setStoreUserMessages,
		setUseEditorContext,
	};
};
